import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
} from "@angular/core";
import { provideRouter, Router } from "@angular/router";
import { APP_BASE_HREF, DecimalPipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
} from "@angular/common/http";

import { provideIonicAngular } from "@ionic/angular/standalone";
import { provideAnimations } from "@angular/platform-browser/animations";
import { Capacitor } from "@capacitor/core";

import { provideTransloco } from "@jsverse/transloco";
import { TranslocoHttpLoader } from "./transloco-http-loader";

import { provideQuangConfig } from "quang";
import { provideAuth } from "quang/auth";
import { quangLoaderInterceptor, withLoaderExcludedUrls } from "quang/loader";

import { APP_ROUTES } from "./app.routes";
import { environment } from "../environments/environment";
import { BASE_PATH } from "./apiModule";
import { provideAngularSvgIcon } from "angular-svg-icon";
import { httpNativeInterceptor } from "./core/http.interceptor";
import { ErrorInterceptor } from "./core/error.interceptor";

import * as Sentry from "@sentry/angular";

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    provideIonicAngular(),
    provideAnimations(),
    DecimalPipe,
    { provide: BASE_PATH, useValue: environment.domain },
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
    provideHttpClient(
      withInterceptors([quangLoaderInterceptor, httpNativeInterceptor]),
      withInterceptorsFromDi(),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideAngularSvgIcon(),
    provideTransloco({
      config: {
        availableLangs: ["en", "it"],
        defaultLang: "en",
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideQuangConfig(
      {
        baseHref: environment.baseHref ?? "/",
        verbose: !environment.production,
      },
      withLoaderExcludedUrls([
        { url: "assets", method: "GET" },
        { url: "rest/v1/attributes/brand", method: "GET" },
        { url: "rest/v1/attributes/year", method: "GET" },
        { url: "rest/v1/attributes/country", method: "POST" },
        {
          method: "GET",
          url: `${environment.keycloakUrl}/realms/${environment.keycloakConfig.realm}/protocol/openid-connect/token`,
        },
      ]),
    ),
    provideAuth({
      issuer: `${environment.keycloakUrl}/realms/${environment.keycloakConfig.realm}`,
      clientId: environment.keycloakConfig.clientId,
      redirectUri: Capacitor.isNativePlatform()
        ? environment.redirectUriApp
        : window.location.origin + window.location.pathname,
      responseType: "code",
      scope: "openid profile email",
      disableAtHashCheck: true,
      showDebugInformation: true,
      autoLogin: true,
      sendAccessToken: true,
      urlsToSendToken: [environment.domain],
      useSilentRefresh: false,
      revokeTokensOnLogout: true,
      getUserProfileOnLoginSuccess: true,
    }),
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
  ],
};
